import { useGetWorkcells } from '@automata/api/apiComponents';
import { WorkcellMode, WorkcellStatus } from '@automata/api/apiSchemas';
import {
  ListScreensHeader,
  PageContainer,
  WorkcellStatusChip,
} from '@automata/ui';
import { getErrorMessage } from '@automata/utils';
import { Box } from '@mui/material';
import { useEffect, useMemo } from 'react';

import ModelList from 'components/ModelList';
import {
  createColumns,
  createRows,
  getNameColumn,
} from 'components/ModelList/columns';
import { FilterIds } from 'components/ModelList/components/FilterPanel';
import { ValidColumnIds } from 'components/ModelList/types';
import { Placeholder } from 'components/Placeholder';
import { useRoute } from 'hooks/useRoute';
import { useURLSearchParams } from 'hooks/useURLSearchParams';
import { useWorkspaceID } from 'hooks/useWorkspaceID';
import { SxStyles } from 'types/global';

import { WorkcellModeIndicator } from './WorkcellModeIndicator';
import WorkcellsListToolbar from './WorkcellsListToolbar';

type RowShape = {
  [ValidColumnIds.ID]: string;
  [ValidColumnIds.NAME]: string;
  [ValidColumnIds.MODE]: WorkcellMode;
  [ValidColumnIds.STATUS]: WorkcellStatus;
};

const styles = {
  dataGridContainer: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    width: '100%',
  },
} satisfies SxStyles;

export const WorkcellsList = (): JSX.Element | null => {
  const workspaceID = useWorkspaceID();
  const [searchParams, setSearchParams] = useURLSearchParams();
  const { [FilterIds.NAME]: nameFilter } = searchParams as {
    [FilterIds.NAME]: string;
  };
  const workcellsRoute = useRoute('workcells');

  const {
    data: currentWorkcells,
    isLoading,
    error,
  } = useGetWorkcells(
    {
      pathParams: { workspaceID: workspaceID || '' },
      queryParams: {
        filter: nameFilter ?? '',
        order_by: 'name',
        order: 'ascending',
        page: 1,
        page_size: 1000,
      },
    },
    { enabled: !!workspaceID }
  );

  // Set default sort to status descending if no sortBy param exists
  useEffect(() => {
    if (!searchParams.sortBy && currentWorkcells?.workcells) {
      setSearchParams({ sortBy: ValidColumnIds.STATUS });
    }
  }, [searchParams.sortBy, currentWorkcells?.workcells, setSearchParams]);

  const columns = useMemo(
    () =>
      createColumns<RowShape>((columnIds) => [
        getNameColumn({
          headerName: 'Workcell Name',
          flex: 4,
        }),
        {
          headerName: 'Mode',
          field: columnIds.MODE,
          flex: 1,
          filterable: false,
          display: 'flex',
          renderCell: (row) => (
            <WorkcellModeIndicator workcellMode={row.value} />
          ),
        },
        {
          headerName: 'Status',
          field: columnIds.STATUS,
          flex: 0.5,
          filterable: false,
          renderCell: (row) => (
            <WorkcellStatusChip workcellStatus={row.value} />
          ),
        },
      ]),
    []
  );

  const rows = useMemo(() => {
    return createRows<RowShape>((columnIds) => {
      if (
        !currentWorkcells?.workcells ||
        !Array.isArray(currentWorkcells.workcells)
      )
        return [];

      return currentWorkcells?.workcells.map((config) => ({
        [columnIds.ID]: config.id,
        [columnIds.NAME]: config.name,
        [columnIds.MODE]: config.mode,
        [columnIds.STATUS]: config.status,
      }));
    });
  }, [currentWorkcells?.workcells]);

  if (error)
    return (
      <Placeholder
        copy={getErrorMessage(error, 'An error occurred fetching workcells')}
      />
    );

  // this prevents the initialState from being set incorrectly when workcells are not loaded
  if (!currentWorkcells) return null;

  return (
    <PageContainer>
      <ListScreensHeader pageName="Workcells" />
      <Box sx={styles.dataGridContainer}>
        <WorkcellsListToolbar />
        <ModelList<RowShape>
          data={{
            columns,
            rows,
            loading: isLoading,
            initialState: {
              sorting: {
                sortModel: [{ field: ValidColumnIds.STATUS, sort: 'desc' }],
              },
            },
          }}
          resourceName="workcells"
          sortableColumns={columns}
          route={workcellsRoute}
          disableToolbar
          disableSearch
        />
      </Box>
    </PageContainer>
  );
};
